import { useCallback, useEffect, useState } from 'react';
import { useApi } from '@backstage/frontend-plugin-api';
import { EffectiveAccess, hasStudioAccess, studioApiRef } from './api';

export type StudioAccess = 'checking' | 'allowed' | 'denied' | 'unavailable';

export function useStudioAccess(tenant: string, groupId: string) {
  const api = useApi(studioApiRef);
  const [access, setAccess] = useState<StudioAccess>('checking');
  const [details, setDetails] = useState<EffectiveAccess>();

  const refresh = useCallback(
    async (signal?: AbortSignal): Promise<boolean> => {
      try {
        const effective = await api.getAccess(tenant, signal);
        const allowed = hasStudioAccess(effective, groupId);
        if (!signal?.aborted) {
          setDetails(effective);
          setAccess(allowed ? 'allowed' : 'denied');
        }
        return allowed;
      } catch {
        if (!signal?.aborted) {
          setDetails(undefined);
          setAccess('unavailable');
        }
        return false;
      }
    },
    [api, groupId, tenant],
  );

  useEffect(() => {
    const controller = new AbortController();
    setAccess('checking');
    setDetails(undefined);
    void refresh(controller.signal);
    const onFocus = () => void refresh(controller.signal);
    window.addEventListener('focus', onFocus);
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible')
        void refresh(controller.signal);
    }, 30_000);
    return () => {
      controller.abort();
      window.removeEventListener('focus', onFocus);
      window.clearInterval(timer);
    };
  }, [refresh]);

  return { access, details, refresh };
}

export function StudioTabVisibility({ visible }: { visible: boolean }) {
  useEffect(() => {
    const update = () => {
      const tab = document.querySelector<HTMLElement>(
        '[data-testid="header-tab-/policy-studio"]',
      );
      if (tab) {
        tab.hidden = !visible;
        tab.style.display = visible ? '' : 'none';
      }
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [visible]);
  return null;
}
